import { useQuery } from "@tanstack/react-query";
import type { getInfoResponseType, partnerElementDataType } from "../types";
import { PartnerApiClien } from "../api-client/api-client";
import { useMemo } from "react";
import { identityPartners } from "@/features/user/types";
import { useUser } from "@/features/user/hooks/use-user";

export const usePartnerInfo = () => {
  const partnerLoginDataQuery = (partner: string | null, page: string) =>
    useQuery<partnerElementDataType, Error, partnerElementDataType>({
      queryKey: ["informations-partner-intro", partner, page],
      queryFn: async () => {
        const res = await PartnerApiClien.getLoginStaticInformationPartner(page);
        return res.data;
      },
      enabled: !!partner,
      retry: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false
    });

  const partnerDashboardDataQuery = (pageName: string, partner: string) =>
    useQuery<partnerElementDataType, Error, partnerElementDataType>({
      queryKey: ["informationsPartnerDashboard"],
      queryFn: async () => {
        const res = await PartnerApiClien.getDahsboardStaticInformationPartner({ pageName });
        return res.data;
      },
      enabled: partner !== null,
      retry: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false
    });

  const partnerUuidQuery = (partner: string | null) =>
    useQuery<
      getInfoResponseType<{ partner_uuid: string }>,
      Error,
      getInfoResponseType<{ partner_uuid: string }>
    >({
      queryKey: ["partnerUUid", partner],
      staleTime: Infinity,
      gcTime: Infinity,
      queryFn: async () => {
        const res = await PartnerApiClien.getPartnerUuid();
        return res;
      },
      enabled: !!partner,
      refetchOnWindowFocus: false
    });

  const getCurrentIdentityPartner = () => {
    const { useUserProfile } = useUser();
    const { data: user } = useUserProfile();

    const { data: dataPartner } = partnerUuidQuery(
      (sessionStorage.getItem("origin") as string) ?? null
    );

    const identityPartner = useMemo(() => {
      if (!user?.identityPartners || !dataPartner) {
        return null;
      }

      const found = user?.identityPartners?.find(
        (partner: identityPartners) => partner.uuid === dataPartner?.data.partner_uuid
      );

      return found ?? null;
    }, [user, dataPartner]);

    return identityPartner;
  };

  return {
    partnerLoginDataQuery,
    partnerDashboardDataQuery,
    partnerUuidQuery,
    getCurrentIdentityPartner
  };
};
